// Team Action Items Section Component - Editable action items with manual save buttons

import React, { useState } from 'react';
import type { 
  TeamActionItem, 
  TeamActionItemAction,
  UseTeamTranscriptDetailReturn 
} from '../../types/teamTranscripts';

interface TeamActionItemsSectionProps {
  teamId: string;
  transcriptId: string;
  actionItems: TeamActionItem[];
  canEdit: boolean;
  saving: boolean;
  onUpdateActionItems: UseTeamTranscriptDetailReturn['updateActionItems'];
  onActionItemsUpdated: () => void;
}

interface ActionItemFormData {
  title: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assigned_to: string;
  due_date: string;
  category: string;
  notes: string;
}

const TeamActionItemsSection: React.FC<TeamActionItemsSectionProps> = ({
  teamId,
  transcriptId,
  actionItems,
  canEdit,
  saving,
  onUpdateActionItems,
  onActionItemsUpdated
}) => {
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [localSaving, setLocalSaving] = useState(false);
  const [formData, setFormData] = useState<ActionItemFormData>({
    title: '',
    description: '',
    status: 'pending',
    priority: 'medium',
    assigned_to: '',
    due_date: '',
    category: '',
    notes: ''
  });

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return 'Unknown date';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      status: 'pending',
      priority: 'medium',
      assigned_to: '',
      due_date: '',
      category: '',
      notes: ''
    });
  };

  const handleAddItem = async () => {
    if (!formData.title.trim()) return;

    setLocalSaving(true);
    try {
      const result = await onUpdateActionItems('add', {
        title: formData.title.trim(),
        description: formData.description.trim(),
        status: formData.status,
        priority: formData.priority,
        assigned_to: formData.assigned_to.trim() || undefined,
        category: formData.category.trim() || undefined,
        notes: formData.notes.trim() || undefined,
        due_date: formData.due_date || undefined
      });
      
      if (result.success) {
        resetForm();
        setIsAddingItem(false);
        onActionItemsUpdated();
      } else {
        alert(result.error || 'Failed to add action item');
      }
    } catch (error) {
      console.error('Failed to add action item:', error);
      alert('Failed to add action item');
    } finally {
      setLocalSaving(false);
    }
  };

  const handleUpdateItem = async (itemId: string) => {
    if (!formData.title.trim()) return;

    setLocalSaving(true);
    try {
      const result = await onUpdateActionItems('update', {
        itemId,
        title: formData.title.trim(),
        description: formData.description.trim(),
        status: formData.status,
        priority: formData.priority,
        assigned_to: formData.assigned_to.trim() || undefined,
        category: formData.category.trim() || undefined,
        notes: formData.notes.trim() || undefined,
        due_date: formData.due_date || undefined
      });
      
      if (result.success) {
        setEditingItemId(null);
        resetForm();
        onActionItemsUpdated();
      } else {
        alert(result.error || 'Failed to update action item');
      }
    } catch (error) {
      console.error('Failed to update action item:', error);
      alert('Failed to update action item');
    } finally {
      setLocalSaving(false);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!confirm('Are you sure you want to delete this action item?')) {
      return;
    }

    setLocalSaving(true);
    try {
      const result = await onUpdateActionItems('delete', { itemId });
      if (result.success) {
        onActionItemsUpdated();
      } else {
        alert(result.error || 'Failed to delete action item');
      }
    } catch (error) {
      console.error('Failed to delete action item:', error);
      alert('Failed to delete action item');
    } finally {
      setLocalSaving(false);
    }
  };

  const handleQuickStatusUpdate = async (itemId: string, newStatus: 'pending' | 'in_progress' | 'completed' | 'cancelled') => {
    setLocalSaving(true);
    try {
      const result = await onUpdateActionItems('update', {
        itemId,
        status: newStatus
      });
      
      if (result.success) {
        onActionItemsUpdated();
      } else {
        alert(result.error || 'Failed to update status');
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status');
    } finally {
      setLocalSaving(false);
    }
  };

  const startEditing = (item: TeamActionItem) => {
    setEditingItemId(item.id);
    setFormData({
      title: item.title,
      description: item.description || '',
      status: item.status,
      priority: item.priority || 'medium',
      assigned_to: item.assigned_to || '',
      due_date: item.due_date || '',
      category: item.category || '',
      notes: item.notes || ''
    });
    setIsAddingItem(false);
  };

  const cancelEditing = () => {
    setEditingItemId(null);
    setIsAddingItem(false);
    resetForm();
  };

  const startAdding = () => {
    setIsAddingItem(true);
    setEditingItemId(null);
    resetForm();
  };

  const isSaving = saving || localSaving;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          ✅ Team Action Items
          <span className="text-sm font-normal text-gray-500">
            ({actionItems.length})
          </span>
        </h3>
        {canEdit && !isAddingItem && !editingItemId && (
          <button
            onClick={startAdding}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Action Item
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {(isAddingItem || editingItemId) && (
        <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            {isAddingItem ? 'Add New Action Item' : 'Edit Action Item'}
          </h4>
          <div className="space-y-3">
            <div>
              <input
                type="text"
                placeholder="Action item title..."
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                maxLength={200}
              />
            </div>
            <div>
              <textarea
                placeholder="Description (optional)..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                maxLength={1000}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            <div>
              <input
                type="text"
                placeholder="Assigned to (optional)..."
                value={formData.assigned_to}
                onChange={(e) => setFormData(prev => ({ ...prev, assigned_to: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                maxLength={100}
              />
            </div>
            <div>
              <input
                type="text"
                placeholder="Category (e.g., follow-up, research)..."
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                maxLength={100}
              />
            </div>
            <div>
              <textarea
                placeholder="Additional notes (optional)..."
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                maxLength={500}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-500">
                {formData.title.length}/200 characters
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={cancelEditing}
                  disabled={isSaving}
                  className="px-3 py-1 text-gray-600 hover:text-gray-800 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={isAddingItem ? handleAddItem : () => handleUpdateItem(editingItemId!)}
                  disabled={isSaving || !formData.title.trim()}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSaving && (
                    <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-white"></div>
                  )}
                  {isAddingItem ? 'Save Action Item' : 'Update Action Item'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Items List */}
      {actionItems.length === 0 && !isAddingItem ? (
        <div className="text-center py-8 text-gray-500">
          <div className="text-4xl mb-3">✅</div>
          <p className="text-sm">
            {canEdit 
              ? 'No action items yet. Add your first action item to get started.' 
              : 'No action items available for this transcript.'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {actionItems.map((item) => (
            <div key={item.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-2">{item.title}</h4>
                  {item.description && (
                    <p className="text-sm text-gray-600 mb-3 whitespace-pre-wrap">
                      {item.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(item.status)}`}>
                      {item.status.replace('_', ' ')}
                    </span>
                    {item.priority && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(item.priority)}`}>
                        {item.priority}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>By: {item.created_by_name}</span>
                    {item.category && (
                      <span>Category: {item.category}</span>
                    )}
                    {item.assigned_to_name && (
                      <span>Assigned: {item.assigned_to_name}</span>
                    )}
                    {item.due_date && (
                      <span>Due: {new Date(item.due_date).toLocaleDateString()}</span>
                    )}
                    <span>Created: {formatDate(item.created_at)}</span>
                  </div>
                  {item.notes && (
                    <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
                      <strong>Notes:</strong> {item.notes}
                    </div>
                  )}
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1 ml-4">
                    {/* Quick status buttons */}
                    {item.status !== 'completed' && (
                      <button
                        onClick={() => handleQuickStatusUpdate(item.id, 'completed')}
                        disabled={isSaving}
                        className="p-1 text-gray-400 hover:text-green-600 transition-colors disabled:opacity-50"
                        title="Mark as completed"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={() => startEditing(item)}
                      disabled={isSaving}
                      className="p-1 text-gray-400 hover:text-blue-600 transition-colors disabled:opacity-50"
                      title="Edit action item"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteItem(item.id)}
                      disabled={isSaving}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                      title="Delete action item"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!canEdit && actionItems.length > 0 && (
        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>You have view-only access to these action items.</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamActionItemsSection;